import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldValues,
  type UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRoot,
  Input,
  Spinner,
  Stack,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import type { BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import type { DemandSuppressionMasterFormValues } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/schema'
import type { SubmitterMasterRow } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { ExpiredMasterAlert } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredMasterAlert'
import { ImmutableInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/ImmutableInput'
import { TopLevelValidPeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/TopLevelValidPeriodFields'
import { SuppressionContractsSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/SuppressionContractsSection'
import { SuppressionPartnerBgsSection } from '~/pages/AdminOcctoMaster/tabs/_plan/components/SuppressionPartnerBgsSection'

type BaseProps = {
  control: Control<DemandSuppressionMasterFormValues>
  errors: FieldErrors<DemandSuppressionMasterFormValues>
  /** list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、PR #5920 レビュー指摘)。 */
  allBgMasters: BgMasterRow[] | undefined
  suppressionBgs: BgMasterRow[]
  partnerBgs: BgMasterRow[]
  businessMasters: BusinessMasterRow[]
  submitterAreaCode: AreaCode | undefined
}

type Props =
  | (BaseProps & {
      isEdit: false
      submitters: SubmitterMasterRow[]
      submitterCandidates: SubmitterMasterRow[]
      isLoadingFormOptions: boolean
      setValue: UseFormSetValue<DemandSuppressionMasterFormValues>
    })
  | (BaseProps & {
      isEdit: true
    })

/**
 * 需要抑制計画マスタの Create / Edit 共通 form fields。
 *
 * Create (isEdit=false): submitterMasterId select で選択 + submitterCode readonly。
 * Edit (isEdit=true): submitterCode を readonly Input で表示 (submitter は immutable)。
 * 他 fields (note / TopLevelValidPeriodFields / Suppression*Section) は共通。
 */
export const DemandSuppressionPlanMasterFormFields = (
  props: Props,
): React.ReactElement => {
  const { t } = useTranslation()
  const {
    control,
    errors,
    allBgMasters,
    suppressionBgs,
    partnerBgs,
    businessMasters,
    submitterAreaCode,
    isEdit,
  } = props
  const idPrefix = isEdit ? 'demandSuppMaster-edit' : 'demandSuppMaster'

  return (
    <Stack gap={4}>
      {/* 適用期間が終了している既存レコードを編集しているときの案内帯。
          新規作成では出さない (「終了している既存データ」を指す文言のため)。 */}
      {isEdit && (
        <ExpiredMasterAlert
          control={control as unknown as Control<FieldValues>}
          idPrefix={idPrefix}
        />
      )}

      {isEdit ? (
        <Controller
          control={control}
          name="submitterCode"
          render={({ field }) => (
            <FieldRoot>
              <FieldLabel htmlFor={`${idPrefix}-submitterCode`}>
                {t(
                  'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterCode',
                )}
              </FieldLabel>
              <ImmutableInput
                {...field}
                id={`${idPrefix}-submitterCode`}
                readOnly
              />
            </FieldRoot>
          )}
        />
      ) : (
        <>
          {/* submitterMasterId — 計画提出者ドロップダウン。 単一 select の正は
              DS `Select` (`~/components/common/FormSelect` の JSDoc 参照)。
              native の `<option value="">…未選択</option>` は placeholder へ移す。 */}
          <FieldRoot invalid={errors.submitterMasterId !== undefined}>
            <FieldLabel>
              {t(
                'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterMaster',
              )}
            </FieldLabel>
            {props.isLoadingFormOptions ? (
              <Spinner />
            ) : (
              <FormSelect
                aria-label={t(
                  'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterMaster',
                )}
                control={control}
                // validFrom 変更で候補外になった選択済み計画提出者を submitters
                // 全件から fallback 解決する。「form value 残置 + 表示空」による
                // silent invalid submission (失効 submitter が RPC 送信される)
                // を防ぐ (BG 側 fallback と同 pattern。AC-OMD-010 は fallback を
                // 明文化していないが、失効値の silent submit は spec 意図に反する)。
                // 失効値は「表示だけ」で menu には出さない (= 再選択させない)。
                fallbackLabel={(v) => {
                  const s = props.submitters.find((x) => x.id === v)
                  return s === undefined
                    ? undefined
                    : `${s.submitterCode}: ${s.submitterName}`
                }}
                name="submitterMasterId"
                onValueChange={(selectedId) => {
                  const found = props.submitters.find(
                    (s) => s.id === selectedId,
                  )
                  props.setValue('submitterCode', found?.submitterCode ?? '')
                }}
                // AC-03: レコードの適用開始日 (validFrom) 時点で有効な
                // 計画提出者のみ候補に出す。
                options={props.submitterCandidates.map((s) => ({
                  value: s.id,
                  label: `${s.submitterCode}: ${s.submitterName}`,
                }))}
                placeholder={t(
                  'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterMasterUnselected',
                )}
              />
            )}
            {errors.submitterMasterId !== undefined && (
              <FieldErrorText>
                {errors.submitterMasterId.message}
              </FieldErrorText>
            )}
          </FieldRoot>

          {/* submitterCode — 読み取り専用 */}
          <Controller
            control={control}
            name="submitterCode"
            render={({ field }) => (
              <FieldRoot invalid={errors.submitterCode !== undefined}>
                <FieldLabel htmlFor={`${idPrefix}-submitterCode`}>
                  {t(
                    'page:adminOcctoMaster.demandSuppressionPlanMaster.form.submitterCode',
                  )}
                </FieldLabel>
                <Input {...field} id={`${idPrefix}-submitterCode`} readOnly />
                {errors.submitterCode !== undefined && (
                  <FieldErrorText>
                    {errors.submitterCode.message}
                  </FieldErrorText>
                )}
              </FieldRoot>
            )}
          />
        </>
      )}

      {/* note (任意) */}
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot invalid={errors.note !== undefined}>
            <FieldLabel htmlFor={`${idPrefix}-note`}>
              {t('page:adminOcctoMaster.demandSuppressionPlanMaster.form.note')}
            </FieldLabel>
            <Textarea
              {...field}
              id={`${idPrefix}-note`}
              value={field.value ?? ''}
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      <TopLevelValidPeriodFields
        control={control as unknown as Control<FieldValues>}
        idPrefix={idPrefix}
        validFromError={errors.validFrom?.message}
        validToError={errors.validTo?.message}
      />

      {/* 需要抑制契約 編集 UI (RG5, RG6): suppressionBgs=DR区分フィルタ済み、partnerBgs=全区分。
            有効期間フィルタ + 削除済み判定 rework は allBgMasters 経由 (AC-03/AC-06)。 */}
      <SuppressionContractsSection
        allBgMasters={allBgMasters}
        businessMasters={businessMasters}
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.demandSuppressionPlanMaster"
        partnerBgs={partnerBgs}
        selfAreaCode={submitterAreaCode}
        suppressionBgs={suppressionBgs}
      />

      {/* 取引先BG (調達/販売) 編集 UI (RG5, RG6): F5 リファクタで 1 instance に統合 */}
      <SuppressionPartnerBgsSection
        allBgMasters={allBgMasters}
        control={control as unknown as Control<FieldValues>}
        i18nNs="adminOcctoMaster.demandSuppressionPlanMaster"
        partnerBgs={partnerBgs}
        selfAreaCode={submitterAreaCode}
      />
    </Stack>
  )
}
